import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { CalendarClock, Globe, MapPin } from 'lucide-react';
import { colors, spacing, typography, borderRadius } from '../../styles/theme';
import { RelatedEventItem } from '../../types/api';
import { formatEventLocalDateTime } from '../../utils/eventDateTime';
import { relationLabel, locationLabel } from '../../utils/relatedEvents';
import { buildEventPath } from '../../utils/slug';
import { DetailSection, DetailSectionTitle } from './DetailSection';
import RememberButton from './RememberButton';

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const Row = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  padding-bottom: ${spacing[3]};
  border-bottom: 1px solid ${colors.border.light};

  &:last-child {
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const RowHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[2]};
`;

const TitleLink = styled(Link)`
  font-size: ${typography.fontSizes.base};
  font-weight: ${typography.fontWeights.medium};
  color: ${colors.text.primary};
  text-decoration: none;

  &:hover {
    color: ${colors.primary[600]};
    text-decoration: underline;
  }
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[2]};
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.secondary};

  svg {
    flex-shrink: 0;
  }
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px ${spacing[2]};
  border-radius: ${borderRadius.full};
  background: ${colors.primary[50]};
  color: ${colors.primary[700]};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.medium};
  white-space: nowrap;
`;

const Skeleton = styled.div`
  height: 56px;
  border-radius: ${borderRadius.md};
  background: ${colors.gray[100]};
  margin-bottom: ${spacing[3]};
`;

const AnchoredSection = styled(DetailSection)`
  scroll-margin-top: ${spacing[8]};
`;

export interface RelatedEventsSectionProps {
  id?: string;
  title: string;
  items: RelatedEventItem[];
  isLoading: boolean;
  onSelect?: (item: RelatedEventItem, index: number) => void;
}

const RelatedEventsSection: React.FC<RelatedEventsSectionProps> = ({
  id,
  title,
  items,
  isLoading,
  onSelect,
}) => {
  if (isLoading) {
    return (
      <AnchoredSection id={id}>
        <DetailSectionTitle>{title}</DetailSectionTitle>
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </AnchoredSection>
    );
  }

  if (items.length === 0) return null;

  return (
    <AnchoredSection id={id}>
      <DetailSectionTitle>{title}</DetailSectionTitle>
      <List>
        {items.map((item, index) => {
          const path = buildEventPath(item) || `/event/${item.eventId}`;
          const where = locationLabel(item);
          const relation = relationLabel(item);
          return (
            <Row key={item.eventId}>
              <RowHeader>
                <TitleLink to={path} onClick={() => onSelect?.(item, index)}>
                  {item.title}
                </TitleLink>
                <RememberButton eventId={item.eventId} actionLocation="related_event" compact />
              </RowHeader>
              <Meta>
                {item.startDate && (
                  <MetaItem>
                    <CalendarClock size={14} aria-hidden="true" />
                    {formatEventLocalDateTime(item.startDate)}
                  </MetaItem>
                )}
                {item.isVirtual ? (
                  <MetaItem>
                    <Globe size={14} aria-hidden="true" />
                    Virtual
                  </MetaItem>
                ) : where ? (
                  <MetaItem>
                    <MapPin size={14} aria-hidden="true" />
                    {where}
                  </MetaItem>
                ) : null}
                {relation && <Chip>{relation}</Chip>}
              </Meta>
            </Row>
          );
        })}
      </List>
    </AnchoredSection>
  );
};

export default RelatedEventsSection;
